import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { createContext, type ReactNode, useContext } from "react"

import type { SX } from "../../theme"
import styles from "./RadioGroup.module.css"

export interface RadioGroupProps<VALUE extends string> {
  values: readonly VALUE[]
  selectedValue: VALUE
  evenItemSize?: boolean
  disabled?: boolean
  sx?: SX
  renderItem: (value: VALUE) => ReactNode
  onChange: (value: VALUE) => void
}

export function RadioGroup<VALUE extends string>(
  props: RadioGroupProps<VALUE>,
) {
  const {
    values,
    selectedValue,
    disabled = false,
    evenItemSize = false,
    sx,
    renderItem,
    onChange,
  } = props

  return (
    <RadioGroupPrimitive.Root
      className={styles.root}
      style={sx}
      disabled={disabled}
      value={selectedValue}
      onValueChange={onChange}
    >
      {values.map((value) => (
        <RadioItemContext.Provider
          key={value}
          value={{ value, isSelected: value === selectedValue, evenItemSize }}
        >
          {renderItem(value)}
        </RadioItemContext.Provider>
      ))}
    </RadioGroupPrimitive.Root>
  )
}

interface RadioItemContextValue {
  value: string
  isSelected: boolean
  evenItemSize: boolean
}

const RadioItemContext = createContext<RadioItemContextValue | undefined>(
  undefined,
)

export function useRadioItem() {
  const context = useContext(RadioItemContext)
  if (!context) throw new Error("useRadioItem must be used within a RadioGroup")
  return context
}
